import * as api from '../../../shared/services/apiClient';
import { AUTH_ENDPOINTS, AUTH_STORAGE_KEYS, AUTH_ERROR_CODES, AUTH_MESSAGES } from '../constants/auth.constants';
import { TokenData, TokenResponse } from '../types/auth.types';

import { _storeSession } from './session';

/**
 * Generic auth error with contextual information
 */
export class AuthError extends Error {
  public readonly code: string;
  public readonly statusCode?: number;

  constructor(message: string, code: string, statusCode?: number) {
    super(message);
    this.name = 'AuthError';
    this.code = code;
    this.statusCode = statusCode;
  }
}

/**
 * Fetches Ping Identity Login URL and PKCE parameters from backend
 */
export const getLoginUrl = async (): Promise<string> => {
  try {
    const response = await api.get(AUTH_ENDPOINTS.LOGIN);
    
    if (!response.ok) {
      throw new Error(`${AUTH_MESSAGES.BACKEND_LOGIN_INIT_FAILED}: ${response.status}`);
    }

    const { url, code_verifier } = await response.json() as { url: string; code_verifier: string };

    // Store verifier for callback (Keeping existing logic)
    sessionStorage.setItem(AUTH_STORAGE_KEYS.CODE_VERIFIER, code_verifier);

    return url;
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Unknown error generating URL';
    throw new AuthError(`Failed to generate auth URL: ${message}`, AUTH_ERROR_CODES.URL_GENERATION_FAILED);
  }
};

/**
 * Handles the OAuth callback, exchanges code for token
 */
export const handleCallback = async (): Promise<TokenData> => {
  const urlParams = new URLSearchParams(window.location.search);
  const code = urlParams.get('code');
  const error = urlParams.get('error');
  const errorDescription = urlParams.get('error_description');

  if (error) {
    throw new AuthError(errorDescription || error, AUTH_ERROR_CODES.OAUTH_CALLBACK_ERROR);
  }
  if (!code) {
    throw new AuthError(AUTH_MESSAGES.NO_AUTH_CODE_RECEIVED, AUTH_ERROR_CODES.MISSING_AUTH_CODE);
  }

  try {
    const verifier = sessionStorage.getItem(AUTH_STORAGE_KEYS.CODE_VERIFIER) || undefined;
    sessionStorage.removeItem(AUTH_STORAGE_KEYS.CODE_VERIFIER);
    
    const tokenData: TokenData = await exchangeCodeForToken(code, verifier);
    
    // Store Session Data
    _storeSession(tokenData);

    return tokenData;
  } catch (error: unknown) {

    if (error instanceof AuthError) {
      throw error;
    }
    const message = error instanceof Error ? error.message : String(error);
    throw new AuthError(`${AUTH_MESSAGES.CALLBACK_HANDLING_FAILED}: ${message}`, AUTH_ERROR_CODES.CALLBACK_FAILED);
  }
};

/**
 * Exchanges authorization code for access token via backend API
 */
export const exchangeCodeForToken = async (code: string, codeVerifier?: string): Promise<TokenData> => {
  const fetchOptions = {
    code,
    code_verifier: codeVerifier,
  };

  let response: Response;
  try {
    response = await api.post(AUTH_ENDPOINTS.TOKEN, fetchOptions);
  } catch (networkError: unknown) {
    const message = networkError instanceof Error ? networkError.message : String(networkError);
    throw new AuthError(`${AUTH_MESSAGES.NETWORK_ERROR_TOKEN_EXCHANGE}: ${message}`, AUTH_ERROR_CODES.TOKEN_EXCHANGE_NETWORK_ERROR);
  }

  if (!response.ok) {
    let errorText: string;
    try {
      errorText = await response.text();
    } catch {
      errorText = AUTH_MESSAGES.UNABLE_TO_READ_ERROR;
    }
    throw new AuthError(`Token exchange failed: ${errorText}`, AUTH_ERROR_CODES.TOKEN_EXCHANGE_FAILED, response.status);
  }

  let tokenData: TokenResponse;
  try {
    tokenData = await response.json() as TokenResponse;
  } catch {
    throw new AuthError(AUTH_MESSAGES.INVALID_TOKEN_JSON, AUTH_ERROR_CODES.TOKEN_RESPONSE_PARSE_ERROR);
  }

  return tokenData as TokenData;
};
